import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import WorkerToggles from '../../components/WorkerToggles';
import { useSettings } from './SettingsContext';
import { awayPhrase, maltaToday } from '../../lib/people';
import { useFeature } from '../../lib/features';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/**
 * Settings -> People & access -> Users.
 *
 * ADMIN AND APPROVER RIGHTS ARE SET HERE AND NOWHERE ELSE (Gordon, 26
 * October). There used to be two places: a Role dropdown on this form that
 * wrote users.role - a mirror the next sign-in rewrote, so it never granted
 * anything - and the Administrators tab, which wrote the list that does. Now
 * there is one Access control per person, PUT /users/:id/role, and every
 * safety rule is the server's: you cannot take your own rights away, the
 * last active administrator and the last approver cannot be removed, and a
 * deactivated person is not given rights. The Administrators tab's address
 * (?tab=admins) opens this page filtered to administrators.
 *
 *   User      no administrator rights
 *   Approver  an administrator who also approves tickets - in the approver
 *             picker and the approvals queue (what every administrator was
 *             before migration 016)
 *   Admin     every administrator right, but not an approver - the director
 *             given the dashboards
 *
 * Somebody listed as an administrator who has never signed in has no user
 * row yet; they are shown at the end ("Awaiting first sign-in") with the same
 * control, which changes their entry on the administrator list directly.
 */
const ACCESS = [
  ['user', 'User'],
  ['approver', 'Approver'],
  ['admin', 'Admin']
];
const ACCESS_WORD = Object.fromEntries(ACCESS);
const FILTERS = [
  ['all', 'Everybody'],
  ['admins', 'Admins'],
  ['approvers', 'Approvers'],
  ['users', 'Users'],
  ['deactivated', 'Deactivated']
];

const accessOf = (u) => (u.is_admin ? (u.is_approver === false ? 'admin' : 'approver') : 'user');

/** What a change of access means, said before it is made. */
function changeSentence(name, from, to) {
  if (to === 'user') return `Take ${name}’s administrator rights away? They keep their account and their tickets.`;
  if (to === 'approver' && from === 'admin') return `Make ${name} an approver too? They appear in the approver picker and the approvals queue.`;
  if (to === 'admin' && from === 'approver') return `Stop ${name} approving tickets? They keep every other administrator right.`;
  if (to === 'approver') return `Make ${name} an approver? They get every administrator right and approve tickets. It takes effect at their next request.`;
  return `Make ${name} an administrator who does not approve? They get every administrator right but stay out of the approver picker.`;
}

/*
 * The Access control in a row: a select, then - before anything is sent - a
 * sentence saying what the change means, with Change and Cancel. Module scope
 * on purpose: declared inside the page it would be a new component on every
 * render, and the select would lose focus the moment the question appeared.
 */
function AccessControl({ rowKey, target, current, active = true, isMe = false, flagReady,
  asked, setAsked, problem, clearProblem, onChange }) {
  const pendingChange = asked && asked.key === rowKey ? asked.to : null;
  return (
    <div className="stn-access">
      <select
        className="stn-access-select"
        aria-label={`Access for ${target.name}`}
        value={pendingChange || current}
        data-access={current}
        onChange={(e) => {
          const to = e.target.value;
          clearProblem(rowKey);
          setAsked(to === current ? null : { key: rowKey, to });
        }}
      >
        {ACCESS.map(([v, l]) => (
          <option key={v} value={v}
            disabled={(v === 'admin' && !flagReady)
              || (v !== 'user' && !active && current === 'user')
              || (v === 'user' && isMe && current !== 'user')}>
            {l}
          </option>
        ))}
      </select>
      {pendingChange && (
        <div className="stn-confirm" role="group" aria-label={`Change access for ${target.name}?`} data-confirm-access="">
          <p>{changeSentence(target.name, current, pendingChange)}</p>
          <div className="stn-confirm-actions">
            <button type="button" className="btn btn-small btn-primary"
              onClick={() => onChange(rowKey, target, pendingChange)}>Change to {ACCESS_WORD[pendingChange]}</button>
            <button type="button" className="btn btn-small btn-secondary" onClick={() => setAsked(null)}>Cancel</button>
          </div>
        </div>
      )}
      {problem && <p className="stn-row-problem" role="alert">{problem}</p>}
    </div>
  );
}

const blankForm = { email: '', display_name: '', role: 'user', department: '', note: '', start_date: '', joiner: null, manager_id: '' };

export default function UsersPage() {
  // 119: each person's licence seats, one click away (administrators only,
  // as this page is), while the Licences switch is on.
  const licencesOn = useFeature('licences');
  const { me } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [flagReady, setFlagReady] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOff, setConfirmOff] = useState(null);
  const [asked, setAsked] = useState(null);          // { key, to } a pending access change
  const [rowProblem, setRowProblem] = useState({});  // key -> sentence
  const [q, setQ] = useState('');
  /*
   * JOINERS & LEAVERS (113): who has a checklist open and which approvers
   * are away, beside each name; whether adding or deactivating starts a
   * checklist by itself; and a link to the one that just started. Empty
   * before 113, when nothing here changes.
   */
  const [people, setPeople] = useState({ ready: false, people: {} });
  const [peopleRules, setPeopleRules] = useState({ joiner_auto: true, leaver_auto: true });
  const [doneLink, setDoneLink] = useState(null);     // { to, label }
  const [offOpts, setOffOpts] = useState({ leaver: true, leaving_date: '' });
  /*
   * LINE MANAGERS (124): who each person reports to - shown under their
   * department and set in the edit form. A catalogue item may send its
   * approval to the requester's line manager; one who is not an approver is
   * passed over, which the row says. Nothing before 124.
   */
  const [managers, setManagers] = useState({ ready: false, managers: {} });

  const filter = useMemo(() => {
    const f = new URLSearchParams(location.search).get('role');
    return FILTERS.some(([k]) => k === f) ? f : 'all';
  }, [location.search]);
  const setFilter = (f) => {
    const p = new URLSearchParams(location.search);
    if (f === 'all') p.delete('role'); else p.set('role', f);
    const qs = p.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const [u, d, a] = await Promise.all([
        // Everybody, switched-off people included, so they can be switched
        // back on from here. Every other list of users leaves them out.
        axios.get(`${API}/users`, { params: { include: 'inactive' } }),
        axios.get(`${API}/departments`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/admins`).catch(() => ({ data: [] }))
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setDepartments(Array.isArray(d.data) ? d.data : []);
      const list = Array.isArray(a.data) ? a.data : [];
      setAdmins(list);
      setFlagReady(!list.some((x) => x.approver_flag_available === false));
      const lm = await axios.get(`${API}/line-managers`).catch(() => null);
      setManagers({ ready: Boolean(lm?.data?.ready), managers: (lm?.data && lm.data.managers) || {} });
      const st = await axios.get(`${API}/people/status`).catch(() => null);
      const ready = Boolean(st?.data?.ready);
      setPeople({ ready, people: (st?.data && typeof st.data.people === 'object' && st.data.people) || {} });
      if (ready) {
        const r = await axios.get(`${API}/people/settings`).catch(() => null);
        if (r?.data && typeof r.data === 'object') setPeopleRules({ joiner_auto: r.data.joiner_auto !== false, leaver_auto: r.data.leaver_auto !== false });
      }
    } catch (e) {
      setProblem(errorOf(e, 'Could not load the users.'));
      setUsers((x) => x || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  /* Administrators listed by address who have no account yet. */
  const pending = useMemo(() => {
    const emails = new Set((users || []).map((u) => String(u.email || '').toLowerCase()));
    return admins.filter((a) => !emails.has(String(a.email || '').toLowerCase()));
  }, [admins, users]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users || []).filter((u) => {
      const on = u.active !== false;
      const acc = accessOf(u);
      if (filter === 'admins' && acc === 'user') return false;
      if (filter === 'approvers' && acc !== 'approver') return false;
      if (filter === 'users' && acc !== 'user') return false;
      if (filter === 'deactivated' && on) return false;
      if (needle && !`${u.display_name || ''} ${u.email || ''} ${u.department || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [users, filter, q]);

  const counts = useMemo(() => {
    const all = users || [];
    return {
      all: all.length + pending.length,
      admins: all.filter((u) => u.is_admin).length + pending.length,
      approvers: all.filter((u) => accessOf(u) === 'approver').length + pending.filter((a) => a.is_approver !== false).length,
      users: all.filter((u) => !u.is_admin).length,
      deactivated: all.filter((u) => u.active === false).length
    };
  }, [users, pending]);
  const showPending = pending.filter((a) => (filter === 'all' || filter === 'admins'
    || (filter === 'approvers' && a.is_approver !== false))
    && (!q.trim() || String(a.email).toLowerCase().includes(q.trim().toLowerCase())));

  const say = (text, link = null) => { setDone(text); setDoneLink(link); setProblem(null); };
  const complain = (text) => { setProblem(text); setDone(null); setDoneLink(null); };
  const checklistLink = (started, kind) => (started && started.id
    ? { to: `/people/runs/${started.id}`, label: started.created === false ? `Their ${kind} checklist` : `Open the ${kind} checklist` }
    : null);

  /* ------------------------------------------------------------- form */

  const save = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { complain('An email address is required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/users/${editing.id}`, {
          email: form.email.trim(), display_name: form.display_name, department: form.department
        });
        if (managers.ready && (managers.managers[editing.id]?.id || '') !== (form.manager_id || '')) {
          await axios.put(`${API}/line-managers/${editing.id}`, { manager_id: form.manager_id || null });
        }
        say(`${form.display_name || form.email} saved.`);
      } else {
        const r = await axios.post(`${API}/users`, {
          email: form.email.trim(), display_name: form.display_name || null,
          department: form.department || null, role: form.role,
          note: form.role !== 'user' && form.note.trim() ? form.note.trim() : undefined,
          ...(people.ready ? { joiner: form.joiner ?? peopleRules.joiner_auto, start_date: form.start_date || undefined } : {})
        });
        const started = r?.data?.joiner && !r.data.joiner.error ? r.data.joiner : null;
        say(`${form.display_name || form.email} added${form.role !== 'user' ? ` as ${ACCESS_WORD[form.role].toLowerCase()}` : ''}. Their account is claimed when they first sign in.${started ? ' Their joiner checklist has started.' : ''}`,
          checklistLink(started, 'joiner'));
      }
      setForm(blankForm);
      setEditing(null);
      await load();
    } catch (err) {
      complain(errorOf(err, editing ? 'Could not save that user.' : 'Could not add that user.'));
    } finally { setSaving(false); }
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email || '', display_name: u.display_name || '', role: accessOf(u), department: u.department || '', note: '',
      manager_id: managers.managers[u.id]?.id || '' });
    setProblem(null);
    setDone(null);
    const el = document.getElementById('s-add');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const cancelEdit = () => { setEditing(null); setForm(blankForm); };

  /* ------------------------------------------------------------ access */

  const changeAccess = async (key, target, to) => {
    setRowProblem((m) => ({ ...m, [key]: null }));
    try {
      if (target.pending) {
        if (to === 'user') await axios.delete(`${API}/auth/admins/${target.id}`);
        else await axios.patch(`${API}/auth/admins/${target.id}/approver`, { is_approver: to === 'approver' });
      } else {
        await axios.put(`${API}/users/${target.id}/role`, { role: to });
      }
      setAsked(null);
      say(`${target.name}: ${ACCESS_WORD[to]}.`);
      await load();
    } catch (err) {
      setRowProblem((m) => ({ ...m, [key]: errorOf(err, 'Could not change that.') }));
    }
  };

  /* ------------------------------------------------------ deactivating */

  const deactivate = async (u) => {
    setConfirmOff(null);
    try {
      const r = await axios.delete(`${API}/users/${u.id}`, people.ready
        ? { data: { leaver: offOpts.leaver, leaving_date: offOpts.leaving_date || undefined } } : undefined);
      if (editing && editing.id === u.id) cancelEdit();
      const started = r?.data?.leaver && !r.data.leaver.error ? r.data.leaver : null;
      say(`${u.display_name || u.email} is deactivated.${started ? (started.created === false ? ' Their leaver checklist was already open.' : ' Their leaver checklist has started.') : ''}`,
        checklistLink(started, 'leaver'));
      await load();
    } catch (err) { complain(errorOf(err, 'Failed to deactivate user')); }
  };
  const askOff = (u) => {
    const open = people.people?.[u.id]?.leaver;
    setOffOpts({ leaver: !open && peopleRules.leaver_auto, leaving_date: maltaToday() });
    setConfirmOff(u.id);
  };
  const startLeaver = async (u) => {
    try {
      const r = await axios.post(`${API}/people/runs`, { kind: 'leaver', user_id: u.id });
      navigate(`/people/runs/${r.data.run.id}`, { state: { created: r.data.created } });
    } catch (err) { complain(errorOf(err, 'Could not start the leaver checklist.')); }
  };
  const reactivate = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { active: true });
      say(`${u.display_name || u.email} is active again.`);
      await load();
    } catch (err) { complain(errorOf(err, 'Failed to reactivate user')); }
  };

  const deptOptions = useMemo(() => {
    const names = departments.map((d) => d.name);
    return form.department && !names.includes(form.department) ? [...names, form.department] : names;
  }, [departments, form.department]);

  /* ------------------------------------------------------------ render */

  return (
    <div className="stn-users">
      {problem && (
        <div className="stn-banner is-problem" role="alert">
          <span>{problem}</span>
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setProblem(null)}>Dismiss</button>
        </div>
      )}
      <p className="stn-flash" role="status" aria-live="polite">
        {done || ''}
        {done && doneLink && <> <Link to={doneLink.to} data-checklist-link="">{doneLink.label} →</Link></>}
      </p>

      <section className="stn-card" id="s-add" aria-labelledby="stn-user-form-h">
        <h3 className="stn-card-title" id="stn-user-form-h">
          {editing ? `Edit ${editing.display_name || editing.email}` : 'Add a user before they sign in'}
        </h3>
        {!editing && (
          <p className="stn-help">
            You do not normally need this. Anyone with an @ncmb.eu address gets an account the first
            time they sign in. Add somebody here to give them work, or administrator rights, before
            they have signed in &mdash; their account is claimed when they do.
          </p>
        )}
        <form className="stn-form" onSubmit={save} noValidate>
          <div className="stn-form-grid">
            <div className="stn-field">
              <label htmlFor="stn-u-email">Email <span className="cfm-req" aria-hidden="true">*</span></label>
              <input id="stn-u-email" type="email" value={form.email} placeholder="name@ncmb.eu"
                     onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="stn-field">
              <label htmlFor="stn-u-name">Display name</label>
              <input id="stn-u-name" type="text" value={form.display_name} placeholder="Rita Gatt"
                     onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            </div>
            <div className="stn-field">
              <label htmlFor="stn-u-dept">Department</label>
              <select id="stn-u-dept" value={form.department}
                      onChange={(e) => setForm({ ...form, department: e.target.value })}>
                <option value="">None</option>
                {deptOptions.map((d) => (
                  <option key={d} value={d}>{departments.some((x) => x.name === d) ? d : `${d} (not in Departments)`}</option>
                ))}
              </select>
            </div>
            {editing && managers.ready && (
              <div className="stn-field">
                <label htmlFor="stn-u-manager">Line manager</label>
                <select id="stn-u-manager" value={form.manager_id}
                        onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                  <option value="">None</option>
                  {(users || []).filter((x) => x.id !== editing.id && (x.active !== false || x.id === form.manager_id)).map((x) => (
                    <option key={x.id} value={x.id}>{x.display_name || x.email}</option>
                  ))}
                </select>
              </div>
            )}
            {!editing && (
              <div className="stn-field">
                <label htmlFor="stn-u-role">Access</label>
                <select id="stn-u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {ACCESS.map(([v, l]) => <option key={v} value={v} disabled={v === 'admin' && !flagReady}>{l}</option>)}
                </select>
              </div>
            )}
            {!editing && people.ready && (
              <div className="stn-field">
                <label htmlFor="stn-u-start">Start date</label>
                <input id="stn-u-start" type="date" value={form.start_date}
                       onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
            )}
            {!editing && people.ready && (
              <div className="stn-field stn-field-wide">
                <label className="stn-inline-check">
                  <input type="checkbox" checked={form.joiner ?? peopleRules.joiner_auto}
                         onChange={(e) => setForm({ ...form, joiner: e.target.checked })} data-joiner-check="" />
                  <span>Start their joiner checklist{form.start_date ? '' : ' (dated from today)'}</span>
                </label>
              </div>
            )}
            {!editing && form.role !== 'user' && (
              <div className="stn-field stn-field-wide">
                <label htmlFor="stn-u-note">Why (optional)</label>
                <input id="stn-u-note" type="text" value={form.note} placeholder="e.g. covers approvals while Marco is away"
                       onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            )}
          </div>
          {editing && <p className="stn-help">Their access is changed in the table below.</p>}
          <div className="stn-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add user'}
            </button>
            {editing && <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>}
          </div>
        </form>
      </section>

      <section className="stn-card" id="s-access" aria-labelledby="stn-access-h">
        <h3 className="stn-card-title" id="stn-access-h">Administrators and approvers</h3>
        <dl className="stn-defs">
          <div><dt>Approver</dt><dd>An administrator who also approves tickets: in the approver picker and the approvals queue.</dd></div>
          <div><dt>Admin</dt><dd>Every administrator right, but not an approver &mdash; for somebody who needs the dashboards, not the approvals.</dd></div>
          <div><dt>User</dt><dd>Everybody else. Signs in with their @ncmb.eu address without being set up here.</dd></div>
        </dl>
        <p className="stn-help">
          Somebody has to stay an approver: a ticket raised without a named approver waits for whoever is.
          You cannot take your own rights away, and the last active administrator cannot be removed.
          {!flagReady && ' Admin without approving needs migration 016 — run npm run migrate:016.'}
        </p>
      </section>

      <section id="s-users" aria-label="Users">
        <div className="stn-toolbar">
          <div className="stn-chips" role="radiogroup" aria-label="Show">
            {FILTERS.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={filter === k}
                      className={`stn-chip${filter === k ? ' is-on' : ''}`} data-filter={k}
                      onClick={() => setFilter(k)}>
                {l}<span className="stn-chip-n">{counts[k]}</span>
              </button>
            ))}
          </div>
          <label className="stn-find">
            <span className="stn-sr">Find a person</span>
            <input type="search" value={q} placeholder="Find a person…" onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>

        {users === null ? <p className="stn-quiet">Loading…</p> : (
          <div className="stn-table-wrap">
            <table className="stn-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Access</th>
                  <th scope="col">Department</th>
                  <th scope="col">Status</th>
                  <th scope="col">Work</th>
                  <th scope="col"><span className="stn-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  // NULL is a row from before the column mattered: active,
                  // exactly as the server treats it.
                  const on = u.active !== false;
                  const name = u.display_name || u.email;
                  const isMe = Boolean(me?.id) && me.id === u.id;
                  const key = `u:${u.id}`;
                  const mine = people.people?.[u.id] || {};
                  return (
                    <tr key={u.id} data-user-row={u.email} className={on ? undefined : 'is-deactivated'}>
                      <td>
                        <strong className="stn-person">{u.display_name || 'N/A'}{isMe && <span className="stn-you">you</span>}</strong>
                        <span className="stn-email">{u.email}</span>
                        {u.admin_note && <span className="stn-note">{u.admin_note}</span>}
                        {(mine.joiner || mine.leaver || mine.away) && (
                          <span className="stn-people-tags" data-people-tags="">
                            {mine.joiner && <Link className="stn-people-tag is-joiner" to={`/people/runs/${mine.joiner.id}`}>Joiner checklist</Link>}
                            {mine.leaver && <Link className="stn-people-tag is-leaver" to={`/people/runs/${mine.leaver.id}`}>Leaver checklist</Link>}
                            {mine.away && <Link className="stn-people-tag is-away" to="/people/away">{awayPhrase(mine.away).replace(/^away/, 'Away')}</Link>}
                          </span>
                        )}
                      </td>
                      <td>
                        <AccessControl rowKey={key} target={{ id: u.id, name }} current={accessOf(u)}
                                       active={on} isMe={isMe} flagReady={flagReady} asked={asked} setAsked={setAsked} problem={rowProblem[key]} clearProblem={(k) => setRowProblem((m) => ({ ...m, [k]: null }))} onChange={changeAccess} />
                      </td>
                      <td>
                        {u.department || '—'}
                        {managers.managers[u.id] && (
                          <span className="stn-email" data-manager="">
                            {`Manager: ${managers.managers[u.id].name}${managers.managers[u.id].approver ? '' : ' (not an approver)'}`}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${on ? 'badge-resolved' : 'badge-closed'}`} data-user-status="">
                          {on ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      {/* Who also fixes tickets, and whether they invoice for it.
                          Asked here rather than on the contractors page because
                          the question is about a person who is already a user -
                          ticking it is what puts them in the contractors list. */}
                      <td><WorkerToggles user={u} onSaved={load} /></td>
                      <td>
                        <div className="stn-row-actions">
                          <button type="button" onClick={() => startEdit(u)} className="btn btn-small btn-secondary"
                                  aria-label={`Edit ${name}`}>Edit</button>
                          {licencesOn && (
                            <Link className="btn btn-small btn-plain" to={`/assets/licences/people/${u.id}`}
                                  aria-label={`Licences of ${name}`} data-licences-link="">Licences</Link>
                          )}
                          {on && confirmOff !== u.id && !isMe && (
                            <button type="button" onClick={() => askOff(u)} className="btn btn-small btn-danger">
                              Deactivate
                            </button>
                          )}
                          {people.ready && !mine.leaver && !isMe && confirmOff !== u.id && (
                            <button type="button" onClick={() => startLeaver(u)} className="btn btn-small btn-secondary"
                                    aria-label={`Start a leaver checklist for ${name}`}>
                              Start leaver
                            </button>
                          )}
                          {!on && (
                            <button type="button" onClick={() => reactivate(u)} className="btn btn-small btn-primary">
                              Reactivate
                            </button>
                          )}
                        </div>
                        {on && confirmOff === u.id && (
                          <div className="stn-confirm" role="group" aria-label={`Deactivate ${name}?`} data-confirm-off="">
                            <p>
                              Deactivate {name}? They will be signed out of Jarvis straight away. Their tickets
                              and history are kept.
                            </p>
                            {people.ready && (mine.leaver ? (
                              <p className="stn-help">Their leaver checklist is already open.</p>
                            ) : (
                              <div className="stn-off-opts">
                                <label className="stn-inline-check">
                                  <input type="checkbox" checked={offOpts.leaver} data-leaver-check=""
                                         onChange={(e) => setOffOpts({ ...offOpts, leaver: e.target.checked })} />
                                  <span>Start their leaver checklist</span>
                                </label>
                                {offOpts.leaver && (
                                  <label className="stn-inline-date">
                                    <span>Leaving date</span>
                                    <input type="date" value={offOpts.leaving_date}
                                           onChange={(e) => setOffOpts({ ...offOpts, leaving_date: e.target.value })} />
                                  </label>
                                )}
                              </div>
                            ))}
                            <div className="stn-confirm-actions">
                              <button type="button" onClick={() => deactivate(u)} className="btn btn-small btn-danger">Deactivate</button>
                              <button type="button" onClick={() => setConfirmOff(null)} className="btn btn-small btn-secondary">Cancel</button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {showPending.map((a) => {
                  const key = `a:${a.id}`;
                  return (
                    <tr key={key} data-user-row={a.email} data-pending="">
                      <td>
                        <strong className="stn-person">{a.email}</strong>
                        <span className="stn-email">Listed as an administrator; has not signed in yet</span>
                        {a.note && <span className="stn-note">{a.note}</span>}
                      </td>
                      <td>
                        <AccessControl rowKey={key} target={{ id: a.id, name: a.email, pending: true }}
                                       current={a.is_approver === false ? 'admin' : 'approver'} flagReady={flagReady} asked={asked} setAsked={setAsked} problem={rowProblem[key]} clearProblem={(k) => setRowProblem((m) => ({ ...m, [k]: null }))} onChange={changeAccess} />
                      </td>
                      <td>—</td>
                      <td><span className="badge badge-on-hold" data-user-status="">Awaiting first sign-in</span></td>
                      <td>—</td>
                      <td />
                    </tr>
                  );
                })}
                {rows.length === 0 && showPending.length === 0 && (
                  <tr><td colSpan={6} className="stn-quiet">Nobody matches.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
